/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCopyRecordInLoop';

const ActionCopyRecordInLoopManager: ActionManagerFn<
  'ActionCopyRecordInLoop'
> = (platform: Platform): ActionManager<'ActionCopyRecordInLoop'> => {
  return {
    ...baseActionManager(platform, 'ActionCopyRecordInLoop'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionSet'] as ActionItemType[]).includes(childType as ActionItemType),
    props: {
      ...baseActionProps,
      sourceAlias: {
        label: 'Source Datastore Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
          'ReadOnlyDataStore',
        ],
      },
      targetAlias: {
        label: 'Target Datastore Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
          'ReadOnlyDataStore',
        ],
      },
      createAsNew: {
        label: 'Create As New',
        type: 'boolean',
      },
    },
    name: 'Copy Record In loop',
    getDisplayText: (item) =>
      `${item.props.sourceAlias ?? ''} -> ${item.props.targetAlias ?? ''}`,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.sourceAlias === alias || item.props.targetAlias === alias,
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Copy Record In Loop action facilitates the programmatic transfer of data records from a specified source datastore to a target datastore. Designed for iterative operations, this action supports nested logic, allowing builders to define specific sub-actions (via an Action Set) to execute during the copy process.

**Key Features**<br/>
* **Broad Datastore Support:** Capable of interfacing with a wide range of data storage types, including Cloud, Local, Transient, Read-Only, and standard DataStores.
* **Visual Flow Indicator:** Automatically generates a display label in the format SourceAlias -> TargetAlias, providing builders with immediate visual confirmation of the data flow direction within the logic tree.
* **Sub-Action Containment:** Explicitly permits the nesting of Action Sets (ActionSet) as child items, enabling complex logic execution or data manipulation within the copy loop.
* **Dependency Tracking:** Automatically registers usage of both Source and Target aliases to ensure data dependencies are maintained within the application.

**Properties**<br/>
* **Source Datastore Alias (Required):** The alias representing the origin of the data records.
  * *Supported Types:* CloudDataStore, DataStore, LocalDataStore, TransientDataStore, ReadOnlyDataStore.
* **Target Datastore Alias (Required):** The alias representing the destination where the records will be copied.
  * *Supported Types:* Same as Source.
* **Create As New:** A boolean configuration that determines whether the transferred records are treated as completely new entities (e.g., generating new primary keys) or retain their original identity context at the destination.

**Use Cases**<br/>
* **Data Archiving:** Moving records from an active CloudDataStore to a ReadOnlyDataStore for historical preservation.
* **Draft Mode Creation:** Copying a record from a persistent DataStore to a Transient or LocalDataStore to allow users to edit a temporary copy without affecting the live data.
* **Bulk Duplication:** Iterating through a list of items to create clones in a target database, potentially modifying specific fields via nested Action Sets during the process.`,
  };
};

export default ActionCopyRecordInLoopManager;
